/**
 * SVG Inline Component
 * Converts SVG img elements to inline SVG based on class or data attributes
 */

class SvgInline {
  constructor(options = {}) {
    this.options = {
      // Default selector for SVG images
      selector: 'img[src$=".svg"]',
      // Class to mark images for inlining
      inlineClass: 'svg-inline',
      // Data attribute to mark images for inlining
      inlineDataAttr: 'data-svg-inline',
      // Class to add to successfully inlined SVGs
      successClass: 'svg-inlined',
      // Class to add to failed inlined SVGs
      errorClass: 'svg-inline-error',
      // Whether to preserve original attributes
      preserveAttributes: true,
      // Whether to add loading states
      addLoadingStates: true,
      // Callback functions
      onSuccess: null,
      onError: null,
      onComplete: null,
      ...options
    };

    this.cache = new Map();
    this.init();
  }

  init() {
    this.inlineSVGs();
  }

  /**
   * Main method to inline SVGs
   */
  inlineSVGs() {
    const selectors = this.buildSelectors();
    
    if (!selectors) {
      return;
    }

    const images = document.querySelectorAll(selectors);

    if (images.length === 0) {
      return;
    }


    images.forEach(img => {
      this.inlineSingleSVG(img);
    });
  }

  /**
   * Build CSS selectors based on options
   */
  buildSelectors() {
    const selectors = [];

    // Add base selector (only if not empty)
    if (this.options.selector) {
      selectors.push(this.options.selector);
    }

    // Add class-based selector
    if (this.options.inlineClass) {
      selectors.push(`img.${this.options.inlineClass}`);
    }

    // Add data attribute selector
    if (this.options.inlineDataAttr) {
      selectors.push(`img[${this.options.inlineDataAttr}]`);
    }

    // If no selectors were added, return empty string
    if (selectors.length === 0) {
      return '';
    }

    return selectors.join(', ');
  }

  /**
   * Inline a single SVG image
   */
  async inlineSingleSVG(img) {
    try {
      // Add loading state
      if (this.options.addLoadingStates) {
        img.classList.add('svg-loading');
      }

      const svgElement = await this.fetchAndParseSVG(img.src);
      
      if (svgElement) {
        this.replaceImageWithSVG(img, svgElement);
        this.handleSuccess(img, svgElement);
      } else {
        throw new Error('Failed to parse SVG');
      }
    } catch (error) {
      this.handleError(img, error);
    }
  }

  /**
   * Fetch and parse SVG content
   */
  async fetchAndParseSVG(url) {
    // Check cache first
    if (this.cache.has(url)) {
      return this.cache.get(url);
    }

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const svgText = await response.text();
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(svgText, 'image/svg+xml');
      
      // Check for parsing errors
      const parserError = svgDoc.querySelector('parsererror');
      if (parserError) {
        throw new Error('SVG parsing error');
      }

      const svgElement = svgDoc.querySelector('svg');
      if (!svgElement) {
        throw new Error('No SVG element found in file');
      }

      // Cache the result
      this.cache.set(url, svgElement.cloneNode(true));
      
      return svgElement;
    } catch (error) {
      console.error(`SvgInline: Error fetching SVG from ${url}:`, error);
      throw error;
    }
  }

  /**
   * Replace img element with inline SVG
   */
  replaceImageWithSVG(img, svgElement) {
    // Preserve original attributes
    if (this.options.preserveAttributes) {
      this.preserveAttributes(img, svgElement);
    }

    // Add success class
    svgElement.classList.add(this.options.successClass);

    // Replace the image
    img.replaceWith(svgElement);
  }

  /**
   * Preserve attributes from img to svg
   */
  preserveAttributes(img, svgElement) {
    const attributesToPreserve = ['id', 'class', 'data-*'];
    const attributesToSkip = ['src'];

    // Preserve all attributes except src
    Array.from(img.attributes).forEach(attr => {
      if (attributesToSkip.includes(attr.name)) {
        return;
      }

      if (attr.name.startsWith('data-') || attributesToPreserve.includes(attr.name)) {
        svgElement.setAttribute(attr.name, attr.value);
      }
    });

    // Copy inline style attribute directly from img to svg
    const imgStyle = img.getAttribute('style');
    if (imgStyle) {
      svgElement.setAttribute('style', imgStyle);
    }

    // Ensure SVG has proper namespace
    if (!svgElement.getAttribute('xmlns')) {
      svgElement.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    }
  }

  /**
   * Handle successful SVG inlining
   */
  handleSuccess(img, svgElement) {
    // Remove loading state
    if (this.options.addLoadingStates) {
      img.classList.remove('svg-loading');
    }

    // Call success callback
    if (this.options.onSuccess && typeof this.options.onSuccess === 'function') {
      this.options.onSuccess(img, svgElement);
    }

  }

  /**
   * Handle SVG inlining error
   */
  handleError(img, error) {
    // Remove loading state
    if (this.options.addLoadingStates) {
      img.classList.remove('svg-loading');
    }

    // Add error class
    img.classList.add(this.options.errorClass);

    // Call error callback
    if (this.options.onError && typeof this.options.onError === 'function') {
      this.options.onError(img, error);
    }

    console.error(`SvgInline: Failed to inline SVG from ${img.src}:`, error);
  }

  /**
   * Public method to inline specific elements
   */
  inlineElements(selector) {
    const elements = document.querySelectorAll(selector);
    elements.forEach(img => {
      if (img.tagName === 'IMG' && img.src.endsWith('.svg')) {
        this.inlineSingleSVG(img);
      }
    });
  }

  /**
   * Public method to clear cache
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Public method to reinitialize
   */
  reinit() {
    this.clearCache();
    this.inlineSVGs();
  }
}

/**
 * Initialize SVG Inline functionality
 */
export function initSvgInline(options = {}) {
  // Default options for the Carleton theme
  const defaultOptions = {
    selector: '', // Empty by default - only use class/data attribute selectors
    inlineClass: 'svg-inline',
    inlineDataAttr: 'data-svg-inline',
    successClass: 'svg-inlined',
    errorClass: 'svg-inline-error',
    preserveAttributes: true,
    addLoadingStates: true,
    onSuccess: (img, svgElement) => {
      // Add any custom success handling here
    },
    onError: (img, error) => {
      // Add any custom error handling here
      console.error('SVG inline failed:', img.src, error);
    }
  };

  const svgInline = new SvgInline({ ...defaultOptions, ...options });
  
  // Make it globally available for manual use
  window.svgInline = svgInline;
  
  return svgInline;
}

// Auto-initialize if DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initSvgInline();
  });
} else {
  initSvgInline();
}
